/* Storage discovery uses one matching variant for type, dimensions, and price. */
(() => {
  if (customElements.get('gp-storage-finder')) return;
  const keys = ['type', 'q', 'placement', 'width', 'budget', 'sort'];
  const defaults = () => ({type:'all',q:'',placement:'',width:'',budget:'',sort:'featured'});
  const dimensions = text => {
    const n = String(text || '').match(/\d+(?:\.\d+)?/g)?.map(Number);
    return n?.length === 3 ? n : null;
  };
  const cleanSize = text => String(text || '').replace(/^W × D × H: |^闊×深×高：/, '');
  class StorageFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.records = [...this.querySelectorAll('.gs-card')].map((el, index) => {
        const data = JSON.parse(el.querySelector('[data-product-data]').textContent);
        const img = el.querySelector('.gs-card__image img');
        el.dataset.currentPrice = Math.min(...data.variants.map(v=>v.price));
        return {el, data, index, img, src:img?.getAttribute('src'), srcset:img?.getAttribute('srcset'), sizes:img?.getAttribute('sizes')};
      });
      this.records.forEach(r => r.data.variants.forEach(v => {v.dimensions = dimensions(v.size);v.types = this.types(r.data, v);}));
      this.querySelectorAll('[data-type-count]').forEach(el => {
        el.textContent = `${this.records.filter(r => r.data.variants.some(v => v.types.includes(el.dataset.typeCount))).length} ${this.labels.count}`;
      });
      this.readURL();
      this.addEventListener('click', e => {
        const type = e.target.closest('[data-type]');
        const clear = e.target.closest('[data-clear]');
        const remove = e.target.closest('[data-remove]');
        const toggle = e.target.closest('[data-toggle]');
        if (type) {e.preventDefault();if(this.clearNative(type.dataset.type))return;this.state.type = type.dataset.type;this.render('push');}
        else if (clear) {if(this.clearNative('all'))return;this.state = defaults();this.render('push');}
        else if (remove) {this.state[remove.dataset.remove] = defaults()[remove.dataset.remove];this.render('push');}
        else if (toggle) {
          const expanded = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(expanded));
          toggle.querySelector('span').textContent = expanded ? '−' : '＋';
          this.querySelector('.gs-advanced').dataset.collapsed = String(!expanded);
        }
      });
      this.addEventListener('keydown', e => {
        if (e.key === ' ' && e.target.matches('a[data-type]')) {e.preventDefault();e.target.click();}
      });
      this.addEventListener('input', e => {
        if (e.target.matches('input[name^="storage_"]')) this.change(e.target, 'replace');
      });
      this.addEventListener('change', e => {
        if (e.target.matches('select[name^="storage_"]')) this.change(e.target, 'push');
        else if (e.target.matches('input[name^="storage_"]')) this.change(e.target, 'replace');
      });
      this.popHandler = () => {this.readURL();this.render();};
      window.addEventListener('popstate', this.popHandler);
      this.render();this.dataset.ready = 'true';
    }
    disconnectedCallback() {window.removeEventListener('popstate', this.popHandler);}
    clearNative(type) {
      const url = new URL(location.href);
      if (![...url.searchParams.keys()].some(k=>k.startsWith('filter.'))) return false;
      [...url.searchParams.keys()].filter(k=>k.startsWith('filter.')||k.startsWith('storage_')||k==='page').forEach(k=>url.searchParams.delete(k));
      if(type!=='all')url.searchParams.set('storage_type',type);
      location.assign(url.href);
      return true;
    }
    types(data, variant) {
      const category = {box:'box',shed:'shed',bench:'seat',wall:'wall',cover:'cover'}[data.category];
      if (category) return [category];
      const form = (variant.form || '').toLowerCase();
      if (form.includes('low')) return ['low'];
      if (form.includes('tall') || form.includes('vertical') || form.includes('tool')) return ['tall'];
      if (/dm-gy/i.test(form) && variant.dimensions) return [variant.dimensions[2] <= 120 ? 'low' : 'tall'];
      const map = {'Tall cabinet':'tall','Low cabinet':'low','Deck box':'box','Shed':'shed','Storage bench':'seat','Wall cupboard':'wall'};
      const forms = Array.isArray(data.forms) ? data.forms : [data.forms];
      return forms.map(f => map[f]).filter(Boolean);
    }
    readURL() {
      const params = new URL(location.href).searchParams;
      this.state = defaults();
      keys.forEach(k => {if (params.has(`storage_${k}`)) this.state[k] = params.get(`storage_${k}`);});
      if (!['all','tall','low','box','shed','seat','wall','cover'].includes(this.state.type)) this.state.type = 'all';
      if (!['','outdoor','sheltered','indoor'].includes(this.state.placement)) this.state.placement = '';
      if (!['featured','price-ascending','price-descending','title-ascending'].includes(this.state.sort)) this.state.sort = 'featured';
      if (!['','2000','3000','5000','10000'].includes(this.state.budget)) this.state.budget = '';
      if (!(Number(this.state.width)>0 && Number(this.state.width)<=10000)) this.state.width = '';
    }
    change(input, historyMode) {
      this.state[input.name.replace('storage_', '')] = input.value;
      this.render(historyMode);
    }
    render(historyMode) {
      const s = this.state, width = Number(s.width), budget = Number(s.budget) * 100;
      this.querySelectorAll('[name^="storage_"]').forEach(el => {const value=s[el.name.replace('storage_','')];if(el.value!==value)el.value=value;});
      this.querySelectorAll('[data-type]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.type === s.type)));
      const tokens = s.q.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
      let count = 0;
      const money = value => new Intl.NumberFormat(this.dataset.lang === 'zh' ? 'zh-HK':'en-HK', {style:'currency',currency:this.dataset.currency || 'HKD',minimumFractionDigits:0,maximumFractionDigits:2}).format(value / 100);
      for (const r of this.records) {
        const variants = r.data.variants.filter(v => {
          const haystack = `${r.data.title} ${r.el.dataset.handle} ${v.title} ${v.form} ${v.label}`.toLocaleLowerCase();
          return (s.type==='all'||v.types.includes(s.type)) && (!width||(!r.data.set && v.dimensions && v.dimensions[0]<=width)) && (!budget||v.price<=budget) && tokens.every(t=>haystack.includes(t));
        });
        r.el.hidden = !variants.length || (s.placement && r.el.dataset.placement!==s.placement);
        if (r.el.hidden) continue;
        count++;
        const matched = [...variants].sort((a,b)=>a.price-b.price)[0];
        const narrowed = s.type !== 'all' || width>0 || budget>0 || variants.length<r.data.variants.length;
        r.el.dataset.currentPrice = matched.price;
        r.el.querySelector('[data-price]').textContent = money(matched.price);
        r.el.querySelector('[data-from]').textContent = variants.some(v=>v.price!==matched.price) ? this.labels.from : '';
        const url = new URL(r.data.url, location.origin);
        if (narrowed) url.searchParams.set('variant', matched.id);
        r.el.querySelectorAll('[data-product-link]').forEach(el=>el.href=url.pathname+url.search);
        r.el.querySelector('[data-size]').textContent = cleanSize(narrowed ? (matched.displaySize || r.data.size) : r.data.size);
        const match = r.el.querySelector('[data-match]');
        match.hidden = !narrowed || new Set(r.data.variants.map(v=>v.form)).size<=1;
        match.textContent = `${this.labels.matched}：${matched.label || matched.form || matched.title}`;
        if (r.img) {
          if (narrowed && matched.image) {r.img.src=matched.image;r.img.removeAttribute('srcset');r.img.removeAttribute('sizes');}
          else {r.img.src=r.src;if(r.srcset)r.img.setAttribute('srcset',r.srcset);if(r.sizes)r.img.setAttribute('sizes',r.sizes);}
        }
      }
      const ordered = [...this.records].sort((a,b)=>{
        if(s.sort==='price-ascending')return Number(a.el.dataset.currentPrice)-Number(b.el.dataset.currentPrice)||a.index-b.index;
        if(s.sort==='price-descending')return Number(b.el.dataset.currentPrice)-Number(a.el.dataset.currentPrice)||a.index-b.index;
        if(s.sort==='title-ascending')return a.data.title.localeCompare(b.data.title,this.dataset.lang==='zh'?'zh-Hant':'en');
        return a.index-b.index;
      });
      const grid = this.querySelector('[data-products]');ordered.forEach(r=>grid.append(r.el));
      this.querySelector('[data-count]').textContent = count;
      this.querySelector('[data-selection-title]').textContent = this.labels[s.type];
      this.querySelector('[data-empty]').hidden = count>0;
      this.querySelector('[data-width-note]').hidden = !(width>0);
      const active = this.querySelector('[data-active]');active.replaceChildren();
      for(const key of ['type','q','placement','width','budget']) {
        if(s[key]===defaults()[key])continue;
        const button=document.createElement('button');button.type='button';button.dataset.remove=key;
        const input=this.querySelector(`[name="storage_${key}"]`);
        const label=key==='type'?this.labels[s.type]:input?.tagName==='SELECT'?input.selectedOptions[0]?.textContent:key==='width'?`${input.parentElement.firstChild.textContent.trim()}: ${s.width}`:s[key];
        button.append(document.createTextNode(label));
        const icon=document.createElement('span');icon.textContent='×';icon.setAttribute('aria-hidden','true');button.append(icon);
        button.setAttribute('aria-label',`${this.dataset.lang==='zh'?'移除':'Remove'} ${label}`);active.append(button);
      }
      active.hidden=!active.childElementCount;
      if(historyMode) {
        const url=new URL(location.href);keys.forEach(k=>{if(s[k]===defaults()[k])url.searchParams.delete(`storage_${k}`);else url.searchParams.set(`storage_${k}`,s[k]);});
        if(url.href!==location.href)history[historyMode==='push'?'pushState':'replaceState']({},'',url);
      }
    }
  }
  customElements.define('gp-storage-finder', StorageFinder);
})();
